// Detail modal behind the asset-view "Out for field inspection" chip.
//
// Shows the headline info of the inspection visit holding the offline lease
// (customer, dates, work type, sites, assigned staff) plus who currently has
// it checked out (open schedule.visit_checkout rows: person, device, taken at,
// last sync). Schedule managers (canManageSchedule) can force-clear a checkout;
// clearing never deletes field work. The device's inspections still upload on
// its next sync, after which it drops its local offline copy.

import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { StatusChip } from '../ui';
import { fetchOfflineCheckoutDetail, offlineClear } from '../../offline/offlineApi';

const fmtDate = (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '—');
const fmtDateTime = (v) => (v ? dayjs(v).format('DD-MM-YYYY HH:mm') : null);

const Field = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: 10, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>{label}</Box>
    <Box sx={{ fontSize: 13.5, color: 'var(--text)', mt: '2px', wordBreak: 'break-word' }}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </Box>
  </Box>
);

export default function OfflineCheckoutModal({ open, visitId, onClose, onCleared }) {
  const canManageSchedule = localStorage.getItem('canManageSchedule') === '1';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false); // stale lease: visit id no longer exists at all
  const [detail, setDetail] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null); // checkout row pending clear confirmation
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState(null);
  const [cleanedUp, setCleanedUp] = useState(false);

  const load = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      setDetail(await fetchOfflineCheckoutDetail(visitId));
    } catch (e) {
      if (e && e.status === 404) setNotFound(true);
      else setError('Could not load checkout details.');
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    if (open) { setDetail(null); setClearError(null); setCleanedUp(false); load(); }
  }, [open, load]);

  const doClear = async () => {
    if (!confirmRow) return;
    setClearing(true);
    setClearError(null);
    try {
      const result = await offlineClear(visitId, confirmRow.id);
      setConfirmRow(null);
      if (result.leaseCleared) {
        setCleanedUp(true); // last row gone: the reloaded empty list shows the success state
        if (onCleared) onCleared();
      }
      await load();
    } catch (_) {
      setClearError('Could not clear the checkout. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  // Stale-lease cleanup: no open checkout rows to clear individually (or the
  // visit is gone entirely), but the asset still carries the lease flags.
  // offline-clear with no checkoutId closes any open rows and, once none
  // remain, clears the per-asset flags, so it doubles as the cleanup call.
  const doCleanup = async () => {
    setClearing(true);
    setClearError(null);
    try {
      const result = await offlineClear(visitId);
      if (result.leaseCleared && onCleared) onCleared();
      setCleanedUp(true);
      if (!notFound) await load();
    } catch (_) {
      setClearError('Could not clean up the checkout. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const visit = detail && detail.visit;
  const checkouts = (detail && detail.checkouts) || [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
            {/* Pinned header */}
            <Box sx={{ flexShrink: 0, px: 3, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <CloudOffIcon sx={{ color: 'var(--warn, #92610a)', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Out for Field Inspection{visitId ? ` · Visit #${visitId}` : ''}
                </Typography>
              </Box>
              {visit && visit.status ? <StatusChip domain="visit" value={visit.status} size="sm" /> : null}
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
              )}
              {!loading && error && <Alert severity="error">{error}</Alert>}

              {!loading && notFound && (
                <>
                  {cleanedUp ? (
                    <Alert severity="success">
                      Cleaned up. The checkout indicator has been removed from this asset.
                    </Alert>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Visit #{visitId} no longer exists, but this asset still shows as checked out
                        to it. This is a stale offline checkout left behind by the removed visit.
                      </Alert>
                      {canManageSchedule ? (
                        <Button variant="contained" color="warning" disabled={clearing} onClick={doCleanup} startIcon={clearing ? <CircularProgress size={14} color="inherit" /> : null}>
                          Clean Up Checkout
                        </Button>
                      ) : (
                        <Box sx={{ fontSize: 12.5, color: 'var(--muted)' }}>
                          A schedule manager can clean this up from this screen.
                        </Box>
                      )}
                    </>
                  )}
                  {clearError && <Alert severity="error" sx={{ mt: 1.5 }}>{clearError}</Alert>}
                </>
              )}

              {!loading && !error && !notFound && visit && (
                <>
                  {visit.deleted_at && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      This visit has been deleted. Its offline checkout is stale; clearing it below
                      removes the indicator from the asset. Any field inspections already completed
                      will still upload when the device next syncs.
                    </Alert>
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '.5px', mb: 1 }}>
                    Inspection Visit
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px 24px', mb: 2.5 }}>
                    <Field label="Customer" value={visit.company} />
                    <Field label="Work Type" value={visit.work_type_name} />
                    <Field label="Start Date" value={fmtDate(visit.start_date)} />
                    <Field label="End Date" value={fmtDate(visit.end_date)} />
                    <Field label="Sites" value={(visit.sites || []).join(', ')} />
                    <Field label="Assigned Staff" value={(visit.staff || []).map((s) => s.name).join(', ')} />
                  </Box>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '.5px', mb: 1 }}>
                    Currently Checked Out By
                  </Typography>
                  {checkouts.length === 0 ? (
                    <Box sx={{ py: 1 }}>
                      {cleanedUp ? (
                        <Alert severity="success">
                          Cleaned up. The checkout indicator has been removed from this asset.
                        </Alert>
                      ) : (
                        <>
                          <Box sx={{ fontSize: 13, color: 'var(--muted)', mb: canManageSchedule ? 1.5 : 0 }}>
                            No one currently has this visit checked out, but the asset still carries
                            the checkout indicator (a stale lease).
                          </Box>
                          {canManageSchedule && (
                            <Button variant="contained" color="warning" size="small" disabled={clearing} onClick={doCleanup} startIcon={clearing ? <CircularProgress size={14} color="inherit" /> : null}>
                              Clean Up Checkout
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {checkouts.map((c) => (
                        <Box key={c.id} sx={{ border: '1px solid var(--border)', borderRadius: '10px', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{c.name || 'Unknown user'}</Box>
                            <Box sx={{ fontSize: 12, color: 'var(--muted)', mt: '2px' }}>
                              Taken offline {fmtDateTime(c.checked_out_at) || '—'}
                              {c.device_label ? ` · ${c.device_label}` : ''}
                            </Box>
                            <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>
                              Last synced: {fmtDateTime(c.last_sync_at) || 'never'}
                            </Box>
                          </Box>
                          {canManageSchedule && (
                            <Button size="small" variant="outlined" color="warning" sx={{ textTransform: 'none', fontWeight: 600 }} onClick={() => { setClearError(null); setConfirmRow(c); }}>
                              Clear Checkout
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {clearError && <Alert severity="error" sx={{ mt: 1.5 }}>{clearError}</Alert>}
                </>
              )}
            </Box>

            {/* Pinned footer */}
            <Box sx={{ flexShrink: 0, px: 3, py: 1.5, borderTop: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <Button variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }} onClick={onClose}>
                Close
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Clear confirmation */}
      <Dialog open={Boolean(confirmRow)} onClose={() => { if (!clearing) setConfirmRow(null); }} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Clear this checkout?</Typography>
          <Typography sx={{ fontSize: 13.5, mb: 1.5 }}>
            This ends {confirmRow && confirmRow.name ? <b>{confirmRow.name}</b> : 'this user'}'s offline checkout of visit #{visitId}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', background: 'var(--info-bg)', borderRadius: '10px', p: 1.25, mb: 2 }}>
            <InfoOutlinedIcon sx={{ color: 'var(--info)', fontSize: 18, mt: '1px' }} />
            <Typography variant="caption" sx={{ color: 'var(--text)' }}>
              No field work is deleted. Inspections already completed on their device will still upload the next
              time it syncs, and the device will then remove its offline copy automatically.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }} disabled={clearing} onClick={() => setConfirmRow(null)}>
              Cancel
            </Button>
            <Button variant="contained" color="warning" disabled={clearing} onClick={doClear} startIcon={clearing ? <CircularProgress size={14} color="inherit" /> : null}>
              Clear Checkout
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
